import { supabase } from '../lib/supabase'

/**
 * The daily round-up.
 *
 * Two beats, chosen by Tom on 12 Sep 2026 and worth holding to: the UAE
 * production industry, and pitch opportunities across the GCC. Not general
 * news, not client PR, not anything else that happens to be interesting. A
 * tab that fills with things nobody acts on is a tab nobody opens, and the
 * way that starts is by widening the beat one reasonable-sounding step at a
 * time.
 *
 * Written each morning by a scheduled task; read-only here. Staff-only by
 * RLS — none of this belongs on a client link.
 *
 * A day with nothing worth reporting is a first-class outcome, stored as
 * `quiet`. Without somewhere honest to record "nothing happened", a daily job
 * pads — and padding is what teaches people to stop reading.
 */

export type NewsCategory = 'industry' | 'opportunity'

export interface NewsItem {
  id: string
  category: NewsCategory
  title: string
  summary: string
  /** One line on why it matters to Portal. Often absent, deliberately. */
  why: string
  sourceName: string
  url: string
  publishedOn: string
  /** Opportunities only: a genuine cut-off, never an event's own start date. */
  deadline: string
}

export interface NewsDay {
  date: string
  /**
   * The day's headline. Short and hard — it is the largest text on the page.
   * Kept separate from `standfirst` because a headline and the sentence
   * explaining it are two different things, and putting both in one field
   * produced a block of display type that read as a paragraph.
   */
  headline: string
  /** One sentence under the headline: what it signals, or why it matters. */
  standfirst: string
  quiet: boolean
  note: string
  items: NewsItem[]
}

function text(value: unknown): string {
  return value === null || value === undefined ? '' : String(value)
}

function itemOf(row: Record<string, unknown>): NewsItem {
  return {
    id: text(row.id),
    category: (text(row.category) as NewsCategory) || 'industry',
    title: text(row.title),
    summary: text(row.summary),
    why: text(row.why),
    sourceName: text(row.source_name),
    url: text(row.url),
    publishedOn: text(row.published_on).slice(0, 10),
    deadline: text(row.deadline).slice(0, 10),
  }
}

/** How many days back the tab keeps. */
export const DIGEST_DAYS = 7

/**
 * The last week of round-ups, newest first.
 *
 * A week rather than only today, because nobody opens this every day. Come
 * back from a shoot on Thursday and Tuesday should still be there — losing
 * the days you were too busy to read is the opposite of useful.
 */
export async function fetchNewsDays(days: number = DIGEST_DAYS): Promise<NewsDay[]> {
  if (!supabase) return []

  const since = new Date()
  since.setDate(since.getDate() - (days - 1))
  const sinceIso = `${since.getFullYear()}-${String(since.getMonth() + 1).padStart(2, '0')}-${String(
    since.getDate(),
  ).padStart(2, '0')}`

  const [dayResult, itemResult] = await Promise.all([
    supabase
      .from('news_days')
      .select('digest_date, headline_line, standfirst, quiet, note')
      .gte('digest_date', sinceIso)
      .order('digest_date', { ascending: false }),
    supabase
      .from('news_items')
      .select('id, digest_date, category, rank, title, summary, why, source_name, url, published_on, deadline')
      .gte('digest_date', sinceIso)
      .order('rank', { ascending: true }),
  ])

  if (dayResult.error) {
    console.warn(`[supabase] news days load failed: ${dayResult.error.message}`)
    return []
  }
  if (itemResult.error) {
    console.warn(`[supabase] news items load failed: ${itemResult.error.message}`)
  }

  const byDate = new Map<string, NewsItem[]>()
  for (const row of itemResult.data ?? []) {
    const record = row as Record<string, unknown>
    const date = text(record.digest_date).slice(0, 10)
    const list = byDate.get(date) ?? []
    list.push(itemOf(record))
    byDate.set(date, list)
  }

  return (dayResult.data ?? []).map((row) => {
    const record = row as Record<string, unknown>
    const date = text(record.digest_date).slice(0, 10)
    return {
      date,
      headline: text(record.headline_line),
      standfirst: text(record.standfirst),
      quiet: Boolean(record.quiet),
      note: text(record.note),
      items: byDate.get(date) ?? [],
    }
  })
}

/** "Saturday 12 September" — and "Today" when it is. */
export function digestDateLabel(iso: string, now: Date = new Date()): string {
  const parsed = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return iso

  const midnight = new Date(now)
  midnight.setHours(0, 0, 0, 0)
  const days = Math.round((midnight.getTime() - parsed.getTime()) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'

  return parsed.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })
}
